import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { apiUrl } from '../../../apiBase';
import { LanguageMenu } from './LanguageMenu';
import { TutorialSplash } from './TutorialSplash';
import { TutorialPlayer } from './TutorialPlayer';
import { TutorialKit } from './TutorialKit';
import { formatBrand } from '../formatNames';
import { clipJobs, finishedClip, type Clip } from '../assemblyClip';
import { READER_BRIGHTNESS } from '../ClipPlayer';
import { shownSubStepNames } from '../subStepName';
import { preloadMeshes } from '../meshPreload';
import { publicMeshUrl, setPublicStamps } from '../publicUrl';
import { serverLog, watchPage } from '../serverLog';
import { useScreenMemory } from '../screenMemory';

// The public, no-login tutorial page reached by scanning a manual's QR code
// (route /tutorials/:slug, mounted outside the app shell — no sidebar or profile chrome).
// Two screens: a product splash first, then the steps. Mobile-first, always the branded
// light look (independent of the visitor's theme). It fetches only published data (the
// backend 404s anything not is_published=1) and needs no auth, so it uses plain fetch (no
// cookies, no axios auth interceptor).
export type PublicSubstep = {
  key: string | null; // sub-step number (1, 2, …); null when the step has a single action
  // Instruction text in both languages, so the page follows its language menu.
  textEn: string;
  textFr: string;
  // AND THE SAME IN BEATS, shown one at a time as the clip plays (see captionAt). Absent or empty
  // on a page published before them.
  beatsEn?: string[];
  beatsFr?: string[];
  // THIS SCREEN'S ANIMATION, or null where Phase 5 signed none off — which is most screens
  // on most manuals, and is why the empty frame is not going anywhere.
  //
  // The same Clip the app publishes, meshes still named by Parts DB run: the movement, and
  // what it moves. Every frame is computed here from those numbers, so the page downloads a
  // couple of glbs and no video at all. `meshUrl` below is where a run becomes an address.
  clip?: Clip | null;
};

export type PublicStep = {
  step: number | null;
  substeps: PublicSubstep[];
};

export type PublicTutorialData = {
  // WHICH DOCUMENT this page is, resolved by the server from whatever address the link
  // carried. Absent from a page served by an older backend, where the address was the doc id.
  docId?: string;
  brand: string | null;
  name: string | null;
  category: string | null;
  // The category's French label from the ontology (null when none); picked over
  // `category` when the page is in FR.
  categoryFr?: string | null;
  time: string | null;
  heroImage?: string | null;
  // The colour/material submodels the visitor can pick between (one per stored variant).
  // Empty/absent for a single-listing notice — the picker then never shows. Each photoUrl
  // is a public route serving that submodel's representative product photo.
  // `variant` identifies the submodel (its id); `label` is what the visitor reads.
  // `stamp`: how far its paint has moved (see setPublicStamps).
  submodels?: { variant: string; label?: string; photoUrl: string; stamp?: number }[];
  // THE TOOLS AND PARTS, shown on two screens before the first step (see TutorialKit). Absent
  // on a page published before them.
  parts?: PublicPart[];
  steps: PublicStep[];
};

export type PublicPart = {
  nameEn: string;
  nameFr: string;
  qty: number | null;
  // The manual's letter for it; absent or empty where it prints none (see TutorialKit).
  letter?: string;
  tool: boolean;
  imageUrl: string | null;
  // Its 3D model; absent on a page published before it (see TutorialKit).
  job?: number | null;
  // In the box or not; absent on a page published before it, which then says neither.
  supplied?: boolean | null;
};

// One screen per sub-step, in step then sub-step order: what the player walks
// through, and what the splash counts as the tutorial's length.
export function tutorialScreens(data: Pick<PublicTutorialData, 'steps'>) {
  const flat = data.steps.flatMap((s) =>
    s.substeps.map((sub) => ({ step: s.step, sub, name: '' })),
  );
  // WHAT EACH SCREEN IS CALLED: its place in its step, which is what the app that published it
  // calls the same sub-step (see shownSubStepNames). Every screen here shows a movement.
  shownSubStepNames(flat.map(({ step }) => ({ step: step ?? 1 })))
    .forEach((name, i) => { flat[i].name = name; });
  // ONE SCREEN PER SUB-STEP, and that is the whole of it.
  //
  // A repeated run used to be walked once per pass — 1B, 1C, 1B, 1C — with a badge saying
  // where in the run the visitor was, because a screen-by-screen reader cannot be asked to
  // remember that a run happened and go back up it. That was the right answer to cycles, and
  // there are no cycles: the analysis writes out every gesture, so the walk simply follows it.
  return flat;
}

// THE PRODUCT STANDING ASSEMBLED: what the last clip leaves, at rest (see finishedClip). The
// player's last screen and the splash's picture. Null where no screen has a clip.
export function tutorialFinished(screens: ReturnType<typeof tutorialScreens>) {
  const last = [...screens].reverse().find((s) => s.sub.clip)?.sub.clip;
  return last ? finishedClip(last) : null;
}

// HOW FAR AHEAD OF THE VISITOR THE MESHES ARE DOWNLOADED, in screens that have a clip — one
// with none has nothing to fetch and is not an advance on anything. Three and not all of them:
// a tutorial is 84 MB of meshes, on a phone's data, and most of it is for screens somebody who
// stops at the second never opens.
const SCREENS_AHEAD = 3;

// What to download, one list of addresses per screen and in the order to do it: the screen at
// `from` when the visitor is ON it (`playing` — on the splash nobody is, and `from` is simply
// the first of the three), then the animated screens after it.
function meshesAhead(
  data: PublicTutorialData, doc: string, from: number, playing: boolean, variant: string,
): string[][] {
  const out: string[][] = [];
  const screens = tutorialScreens(data);
  for (let i = from; i < screens.length && out.length < SCREENS_AHEAD + (playing ? 1 : 0); i++) {
    const clip = screens[i].sub.clip;
    if (clip) out.push([...new Set(clipJobs(clip).map((job) => publicMeshUrl(doc, job, variant)))]);
    // The screen being looked at holds its place even with no clip: what follows is its advance.
    else if (playing && i === from) out.push([]);
  }
  return out;
}

// The classifier emits "OTHER" when no category fits — that is not a label.
const label = (s: string | null) => {
  const v = (s || '').trim();
  return v.toUpperCase() === 'OTHER' ? '' : v;
};

// Best display name for a tutorial, shared by the splash and the player: the category, then
// the product's own name, either alone where the other is missing, qualified by the brand when
// known: "Chaise Josette (Maison Pavane)". The category is localized (French label under FR); the name is a proper
// noun, shown as authored. Never the document's file name. A function declaration (hoisted) so
// the value import from the child components is safe despite the module cycle.
export function tutorialHeading(data: PublicTutorialData, language: 'en' | 'fr'): string {
  const category = label(language === 'fr' && data.categoryFr ? data.categoryFr : data.category);
  const name = label(data.name);
  // A name that already says its category ("Chaise Josette") is not given it twice.
  const both = category && name && !name.toLowerCase().startsWith(category.toLowerCase())
    ? `${category} ${name}` : name || category;
  if (!both) return 'Notice.ia';
  const brand = formatBrand(label(data.brand));
  if (!brand) return both;
  // Keep "(brand)" together as a non-breaking unit, with an ordinary space before
  // it — so when the whole thing doesn't fit on one line, the brand wraps to a
  // second row as a whole instead of breaking mid-brand.
  return `${both} ${`(${brand})`.replace(/ /g, '\u00a0')}`;
}

// How long the splash may wait on its hero image before giving up on it. The
// image is decoration: a slow or stalled one must never hold the tutorial hostage.
const HERO_TIMEOUT_MS = 5000;

// Fetch and decode the splash image, resolving true once the browser holds it
// ready to paint. The hero is a separate request from the tutorial JSON, so
// rendering the moment the JSON lands paints the card and then pops the image in
// a beat later; awaiting this first lets the card appear complete, in one go.
// Resolves false — never rejects — when the image fails or takes too long, so the
// caller can drop it and show the placeholder instead of a broken <img>.
function loadHero(src: string | null | undefined): Promise<boolean> {
  if (!src) return Promise.resolve(false);
  return new Promise((resolve) => {
    const img = new Image();
    const settle = (ok: boolean) => {
      clearTimeout(timer);
      img.onload = img.onerror = null;
      resolve(ok);
    };
    const timer = setTimeout(() => settle(false), HERO_TIMEOUT_MS);
    img.onload = () => settle(true);
    img.onerror = () => settle(false);
    img.src = src;
  });
}

export function PublicTutorial() {
  // THE SLUG THE LINK CARRIED. The server resolves it (see _doc_of); what comes back names the
  // document, which is what the rest of this page — the meshes, the photographs, the
  // completion — is keyed by.
  const { slug: token = '' } = useParams();
  const [doc, setDoc] = useState('');
  const { t } = useLanguage();
  const [data, setData] = useState<PublicTutorialData | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'notfound'>('loading');
  const [view, setView] = useState<'splash' | 'tools' | 'parts' | 'playing'>('splash');
  // HOW BRIGHT THE PICTURE IS, one setting for every visitor: half again the scene's own light,
  // where the visitor's dial used to open. The scene is balanced for a pale seat's colour, and
  // under it a dark wood frame is nearly black on a phone; what this page is for is seeing which
  // hole a screw goes into. The dial itself is gone: one more control over a manual nobody asked
  // to adjust.
  const brightness = READER_BRIGHTNESS;

  // WHERE THE VISITOR IS, IN THE ADDRESS: "?screen=7", one-based, absent on the splash.
  //
  // A tutorial is followed with both hands full and a phone propped against something. It is
  // reloaded — by a tap on the address bar, by a browser reclaiming the tab's memory, by the
  // page being opened again from a notification — and every one of those threw the visitor
  // back to the splash with the whole thing to walk again, because the only record of where
  // they were was a `useState` in the player.
  //
  // The address is the right place for it and not sessionStorage: it survives the reload, it
  // is what the Back button reads, and a screen somebody wants to hand to somebody else is
  // then a link rather than an instruction to press Next six times.
  //
  // REPLACED RATHER THAN PUSHED, so the history stays one entry per page: the player draws
  // its own Back button, and a browser Back that walked screens too would mean leaving the
  // tutorial took as many presses as finishing it.
  const [params, setParams] = useSearchParams();
  const asked = Number(params.get('screen'));
  const at = Number.isFinite(asked) && asked >= 1 ? Math.floor(asked) - 1 : null;
  const [idx, setIdx] = useState(at ?? 0);

  // EVERY CHANGE OF SCREEN, in the load log beside the 3D views' own lines (Louis 2026-10-23): on
  // the same clock as theirs (seconds since the page opened), so a slow view reads against when
  // the visitor arrived on its screen. One logger for the page's life.
  const pageLog = useRef<ReturnType<typeof serverLog>>(undefined);
  useEffect(() => {
    const log = serverLog('load', 'page');
    pageLog.current = log;
    const unwatch = watchPage(log);
    return () => {
      unwatch();
      log?.stop();
      pageLog.current = undefined;
    };
  }, []);
  useEffect(() => {
    // After the logger above, which an effect declared first would find not made yet.
    pageLog.current?.say(view === 'playing' ? `screen step ${idx + 1}` : `screen ${view}`);
  }, [view, idx]);
  // AND WHAT EACH SCREEN ASKS OF THE PHONE, reported per visit for Phase 7's table: named as the
  // app names it ("1-2", by its sub-step), and ordered as the visitor walks it.
  const stepNames = useMemo(() => (data ? tutorialScreens(data).map((s) => s.name) : []), [data]);
  const shown = view !== 'playing' ? { screen: view, ord: ['splash', 'tools', 'parts'].indexOf(view) }
    : idx < stepNames.length ? { screen: stepNames[idx], ord: 3 + idx }
    : { screen: 'done', ord: 3 + stepNames.length };
  useScreenMemory(data && doc ? `/api/notice/public/tutorial/${encodeURIComponent(doc)}/memory` : '', shown.screen, shown.ord);
  const goTo = (next: number) => {
    setIdx(next);
    const q = new URLSearchParams(params);
    q.set('screen', String(next + 1));
    setParams(q, { replace: true });
  };
  // AND WHICH COLOURWAY, beside it in the address and for the same reasons: "?variant=<id>".
  // The first one until the visitor picks; one the tutorial no longer has is the first too.
  const variants = data?.submodels ?? [];
  const variant = variants.find((s) => s.variant === params.get('variant'))?.variant
    ?? variants[0]?.variant ?? '';
  const pickVariant = (next: string) => {
    const q = new URLSearchParams(params);
    q.set('variant', next);
    setParams(q, { replace: true });
  };
  const leave = () => {
    setView('splash');
    // BACK TO THE FIRST SCREEN WITH IT. The splash is the way OUT of a run — "Retour" from
    // screen one, "Home" from the success card — and a splash whose Ready button dropped
    // somebody back into the middle of the tutorial they had just left would be a button
    // that lies about what it does.
    setIdx(0);
    const q = new URLSearchParams(params);
    q.delete('screen');
    setParams(q, { replace: true });
  };

  // When the link was opened, used to time the assembly through to the success screen.
  //
  // KEPT IN sessionStorage, per document, for the same reason the screen is kept in the
  // address: a reload halfway through an assembly used to restart the clock, so the success
  // screen congratulated somebody on doing in forty seconds what took them a quarter of an
  // hour — and that number is POSTed and averaged with everybody else's. The tab is the right
  // scope: one visitor, one sitting.
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;
    // The moment this tutorial was FIRST opened in this tab, not the moment it was last
    // loaded. Written once and read back on every reload; a tutorial opened afresh in a new
    // tab starts its own clock.
    const key = `notice.tutorialStart.${token}`;
    const stored = Number(sessionStorage.getItem(key));
    if (Number.isFinite(stored) && stored > 0) {
      startRef.current = stored;
    } else {
      startRef.current = Date.now();
      try { sessionStorage.setItem(key, String(startRef.current)); } catch { /* private mode */ }
    }
    setState('loading');
    // The splash unless the address names a screen — see `at`, which is what makes a reload
    // land where the visitor was.
    setView(at == null ? 'splash' : 'playing');
    fetch(apiUrl(`/api/notice/public/tutorial/${encodeURIComponent(token)}`))
      .then((r) => {
        if (!r.ok) throw new Error('not found');
        return r.json() as Promise<PublicTutorialData>;
      })
      .then(async (d) => {
        // Hold the loading spinner until the hero is decoded (it then paints from
        // cache, with the rest of the card). Dropped if it never arrives. NOT FETCHED AT ALL
        // where the splash draws the product in 3D instead, which is wherever a step has a
        // clip: it was 270 KB downloaded before anything showed, for a picture nobody saw.
        const ready = !!tutorialFinished(tutorialScreens(d)) || await loadHero(d.heroImage);
        if (alive) {
          setPublicStamps(d.submodels);
          setData(ready ? d : { ...d, heroImage: null });
          // The document this address named — falling back to the address itself, which is
          // what an older server answers with, and which for those pages IS the doc id.
          setDoc(d.docId || token);
          setState('ok');
        }
      })
      .catch(() => {
        if (alive) setState('notfound');
      });
    return () => {
      alive = false;
    };
    // `at` is read once, when the tutorial loads: it is where the ADDRESS said to open, and
    // re-running this every time the visitor pressed Next would re-fetch the tutorial.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // THE MESHES, ASKED FOR BEFORE THE SCREEN THAT DRAWS THEM — from the splash on, so the time
  // spent reading it and choosing a model is time the first clip spends arriving. The screen
  // being looked at first, then the animated ones after it; re-said at every move, which
  // costs nothing for what was already asked for (see meshPreload).
  useEffect(() => {
    // NOT ON THE SPLASH: its colourway tiles are downloading the same meshes, and a second request
    // for one made the browser hold it behind the first and then ask the server again — the
    // picked colourway's tile was always the last to show (Louis 2026-10-23). From the tools
    // screen on, the tiles are gone and the steps are next.
    if (!data || !doc || view === 'splash') return;
    preloadMeshes(meshesAhead(data, doc, idx, view === 'playing', variant));
  }, [data, doc, idx, view, variant]);

  // THE SCREENS BEFORE THE FIRST STEP: the tools, then the parts, each where there is any.
  const tools = (data?.parts ?? []).filter((p) => p.tool);
  const pieces = (data?.parts ?? []).filter((p) => !p.tool);
  const kit = [...(tools.length ? ['tools' as const] : []), ...(pieces.length ? ['parts' as const] : [])];
  const play = () => { setView('playing'); goTo(0); };

  let content: React.ReactNode;
  if (state === 'loading') {
    content = (
      <div className="flex flex-1 items-center justify-center gap-2 font-satoshi text-sm text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t('notice.public.loading')}
      </div>
    );
  } else if (state === 'notfound' || !data) {
    content = (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center font-satoshi">
        <h1 className="font-sora text-lg font-bold text-slate-800">
          {t('notice.public.notFound')}
        </h1>
        <p className="text-sm text-slate-500">{t('notice.public.notFoundHint')}</p>
      </div>
    );
  } else if (view === 'splash') {
    content = (
      <TutorialSplash
        docId={doc}
        data={data}
        brightness={brightness}
        variant={variant}
        onVariant={pickVariant}
        onStart={() => (kit.length ? setView(kit[0]) : play())}
      />
    );
  } else if (view === 'tools' || view === 'parts') {
    const at = kit.indexOf(view);
    content = (
      <TutorialKit
        kind={view}
        parts={view === 'tools' ? tools : pieces}
        at={at + 1}
        of={kit.length}
        docId={doc}
        data={data}
        brightness={brightness}
        variant={variant}
        onNext={() => (at + 1 < kit.length ? setView(kit[at + 1]) : play())}
        onBack={() => (at > 0 ? setView(kit[at - 1]) : leave())}
      />
    );
  } else {
    content = (
      <TutorialPlayer
        docId={doc}
        data={data}
        idx={idx}
        onIdx={goTo}
        // Back from the first step is back to the parts, where there are any.
        onExit={() => (kit.length ? setView(kit[kit.length - 1]) : leave())}
        onHome={leave}
        startedAt={startRef.current}
        brightness={brightness}
        variant={variant}
      />
    );
  }

  // Page-level column, the content growing to fill it on every screen (splash, player, loading,
  // error). No "Made with NoticeIA" under it: the page is the manufacturer's manual, and a credit
  // to the tool that made it is a line about us on a page about their chair. The language menu is
  // pinned top-right over all of them; it only translates the UI chrome — the manual's own text
  // comes from the publish snapshot in its authored language.
  // THE HEIGHT THE READER SEES (dvh), not 100vh: on a phone that is the height with the browser's
  // bars hidden, taller than the screen while they show, and the page scrolled by the difference
  // under a frame sized to the visible height (Louis 2026-10-23).
  // AND NOTHING ON IT SELECTABLE (Louis 2026-10-23): a long press on a phone selected the cards
  // and opened the copy menu over them. Nor the long-press callout iOS gives a picture.
  return (
    <div className="flex min-h-dvh select-none flex-col bg-white [-webkit-touch-callout:none]">
      {content}
      <div className="fixed right-3 top-3 z-50">
        <LanguageMenu />
      </div>
    </div>
  );
}
